import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, split } from "@apollo/client";
import { setContext } from "@apollo/link-context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import React, { useEffect, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ACCOUNT_CONTEXT_ID_HEADER_NAME,
  ATMTS_REQUEST_ORIGIN_HEADER_NAME,
  ATMTS_REQUEST_ORIGIN_HEADER_VALUE,
} from "common/constants";
import createApolloInMemoryCache from "common/graphql/createApolloInMemoryCache";
import { temporaryToken } from "@app.automotus.io/components/hooks";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { Client as GraphQLWsClient, createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";
import { AccountContext } from "@app.automotus.io/components/context/AccountContext";

const cache = createApolloInMemoryCache();

/**
 * Apollo context provider that uses Auth0 to fetch a token and inserts a few additional links
 * for error handling. When an AccountContextProvider is mounted above this component,
 * every outbound request carries X-Account-Context-Id. When no provider is present
 * (e.g. CurbPass), the header is silently omitted.
 */
const AuthorizedApolloProvider: React.FC = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  // Read account context directly so we degrade gracefully when no AccountContextProvider is mounted.
  // DEFAULT_ACCOUNT_CONTEXT supplies contextId=null and a no-op setContextId.
  const { contextId, setContextId } = React.useContext(AccountContext);

  // Sync latest values into refs so link callbacks never hold stale closures.
  // These assignments are intentionally in the render body rather than in a useEffect.
  // Link callbacks (authLink, wsLink, errorLink) can fire between renders; a useEffect
  // sync would introduce a one-render lag during which a request could go out with a
  // stale contextId or token. Synchronous assignment during render ensures callbacks
  // always read the value from the most recent render.
  const contextIdRef = useRef<string | null>(contextId);
  contextIdRef.current = contextId;

  const setContextIdRef = useRef<(id: string | null) => void>(setContextId);
  setContextIdRef.current = setContextId;

  // Track previous contextId to detect switches vs. first mount vs. logout.
  const previousContextIdRef = useRef<string | null>(null);

  // Hold the graphql-ws client so we can force a reconnect on context switch.
  // connectionParams is only evaluated at socket connect time, so an open
  // subscription would otherwise keep streaming under the old X-Account-Context-Id.
  const wsClientRef = useRef<GraphQLWsClient | null>(null);

  const uri = process.env.REACT_APP_GRAPHQL_ENDPOINT || "";

  // useMemo ensures the client is created once and only recreates when uri or
  // isAuthenticated changes. All link callbacks read from refs so they always
  // have fresh values without being listed as memo deps (which would cause
  // unnecessary client recreation).
  // Note: useMemo is declared before the early return to satisfy the rules-of-hooks
  // requirement that hooks are never called conditionally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const apolloClient = useMemo(() => {
    const httpLink = createHttpLink({ uri });

    const wsClient = createClient({
      url: uri.replace(/https?/, "wss"),
      connectionParams: async () => {
        let token: string | undefined;
        if (isAuthenticated) {
          token = await getAccessTokenSilently();
        } else {
          token = temporaryToken();
        }

        return {
          headers: {
            [ATMTS_REQUEST_ORIGIN_HEADER_NAME]: ATMTS_REQUEST_ORIGIN_HEADER_VALUE,
            ...(token && { Authorization: isAuthenticated ? `Bearer ${token}` : token }),
            ...(contextIdRef.current && { [ACCOUNT_CONTEXT_ID_HEADER_NAME]: contextIdRef.current }),
          },
        };
      },
    });
    wsClientRef.current = wsClient;

    const wsLink = new GraphQLWsLink(wsClient);

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      wsLink,
      httpLink,
    );

    const authLink = setContext(async (_, { headers, ...context }) => {
      let token: string | undefined;
      if (isAuthenticated) {
        token = await getAccessTokenSilently();
      } else {
        token = temporaryToken();
      }

      return {
        headers: {
          ...headers,
          [ATMTS_REQUEST_ORIGIN_HEADER_NAME]: ATMTS_REQUEST_ORIGIN_HEADER_VALUE,
          ...(token && { Authorization: isAuthenticated ? `Bearer ${token}` : token }),
          ...(contextIdRef.current && { [ACCOUNT_CONTEXT_ID_HEADER_NAME]: contextIdRef.current }),
        },
        ...context,
      };
    });

    // On 401 with X-Account-Context-Id present: clear context and retry once without the header.
    // setContextId(null) triggers a React re-render, but setting contextIdRef.current=null immediately
    // ensures the retry's authLink call won't re-add the header before the re-render propagates.
    const errorLink = onError(({ networkError, operation, forward }) => {
      const statusCode = networkError && "statusCode" in networkError ? networkError.statusCode : undefined;
      if (statusCode !== 401 || !contextIdRef.current) {
        return;
      }

      contextIdRef.current = null;
      setContextIdRef.current(null);

      return forward(operation);
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, authLink, new RetryLink(), splitLink]),
      cache,
      connectToDevTools: true,
    });
  }, [uri, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // On a truthy→truthy contextId switch: clear the HTTP cache AND force the
  // graphql-ws socket to reconnect so live subscriptions re-run connectionParams
  // with the new X-Account-Context-Id. First mount (prev=null→any) and
  // logout/fallback (any→null) are intentionally excluded.
  //
  // terminate() is used (not dispose()) because dispose() is fatal — the client
  // would refuse future subscriptions. terminate() force-closes the socket with
  // a synthetic 4499 close event; graphql-ws then reconnects and Apollo
  // re-subscribes any active operations against the fresh connection.
  //
  // apolloClient is a dep so the effect always references the current client; it
  // only changes when isAuthenticated flips, at which point the condition below
  // won't fire (contextId doesn't change during an auth transition).
  useEffect(() => {
    if (previousContextIdRef.current !== null && previousContextIdRef.current !== contextId && contextId !== null) {
      apolloClient.clearStore().catch(() => undefined);
      wsClientRef.current?.terminate();
    }
    previousContextIdRef.current = contextId;
  }, [contextId, apolloClient]);

  // Early return after all hooks so the rules-of-hooks constraint is satisfied.
  if (!uri) {
    return null;
  }

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;
